import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  IconButton,
  Switch,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridFooterContainer } from "@mui/x-data-grid";
import { Add, Edit, Circle, Close } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import { tableStyle } from "../../components";
import { CssTextField } from "../../components/TextField";
import {
  addNewCompany,
  listAllCompanies,
  resetAllCompanies,
  updateCompany,
} from "../../store/actions/companiesActions";

export const CompanyDialog = ({ open, onClose, onSubmit, data, colors }) => {
  const isUpdate = Boolean(data);

  const [form, setForm] = useState({
    _id: "",
    name: "",
    email: "",
    mobile: "",
    address: "",
    gst_number: "",
    is_active: true,
  });

  useEffect(() => {
    if (open) {
      setForm({
        _id: data?._id || "",
        name: data?.name || "",
        email: data?.email || "",
        mobile: data?.mobile || "",
        address: data?.address || "",
        gst_number: data?.gst_number || "",
        is_active: data?.is_active ?? true,
      });
    }
  }, [data, open]);

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setForm({ ...form, mobile: value });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          bgcolor: colors.blueAccent?.[700],
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" fontWeight="bold">
          {isUpdate ? "Update Company" : "Add New Company"}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: colors.primary[400], pt: 3 }}>
        <Stack spacing={2} mt={1}>
          <CssTextField
            label="Company Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            size="small"
            required
          />

          <CssTextField
            type="email"
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            fullWidth
            size="small"
            required
          />

          <CssTextField
            label="Mobile"
            value={form.mobile}
            onChange={handleMobileChange}
            fullWidth
            size="small"
            inputProps={{ inputMode: "numeric" }}
          />

          <CssTextField
            label="Address"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
            fullWidth
            size="small"
          />

          <CssTextField
            label="GST Number"
            value={form.gst_number}
            onChange={(e) => setForm({ ...form, gst_number: e.target.value })}
            fullWidth
            size="small"
          />

          {isUpdate && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ bgcolor: colors.primary[400], p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSubmit(form)}>
          {isUpdate ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const CompaniesList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState(null);

  const { isLoading, companiesList, showViewMore } = useSelector(
    ({ loading, companies }) => {
      const root = companies?.companies || companies;
      return {
        isLoading: loading,
        companiesList: root?.list || [],
        showViewMore: root?.showViewMore || false,
      };
    }
  );

  useEffect(() => {
    dispatch(resetAllCompanies());
    dispatch(listAllCompanies());
  }, [dispatch]);

  const fetchMoreData = () => dispatch(listAllCompanies());

  const CustomFooter = () => (
    <GridFooterContainer>
      <Stack width="100%" alignItems="center">
        {showViewMore ? (
          <Button fullWidth onClick={fetchMoreData}>
            <b>View More</b>
          </Button>
        ) : (
          <Typography variant="h6">All Data Listed</Typography>
        )}
      </Stack>
    </GridFooterContainer>
  );

  const handleAdd = () => {
    setDialogData(null);
    setDialogOpen(true);
  };

  const handleUpdate = (row) => {
    setDialogData(row);
    setDialogOpen(true);
  };

  const handleSubmit = (form) => {
    if (dialogData) {
      dispatch(updateCompany({ data: form }));
    } else {
      dispatch(addNewCompany(form));
    }
    setDialogOpen(false);
    setDialogData(null); 
  };

  const columns = [
    {
      field: "",
      headerName: "#",
      width: 50,
      renderCell: (params) => params.api.getRowIndex(params.row._id) + 1,
    },
    { field: "name", headerName: "Company Name", width: 200 },
    { field: "email", headerName: "Email", width: 220 },
    { field: "mobile", headerName: "Mobile", width: 140 },
    { field: "address", headerName: "Address", width: 180 },
    {
      field: "is_active",
      headerName: "Status",
      width: 120,
      renderCell: ({ row }) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Circle sx={{ fontSize: 10, color: row.is_active ? "green" : "red" }} />
          {row.is_active ? "Active" : "Inactive"}
        </Box>
      ),
    },
    {
      field: "update",
      headerName: "Update",
      width: 140,
      renderCell: ({ row }) => (
        <Button
          size="small"
          variant="outlined"
          endIcon={<Edit fontSize="small" />}
          onClick={() => handleUpdate(row)}
        >
          Update
        </Button>
      ),
    },
  ];

  return (
    <Box m="20px" mb={15}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Header title="COMPANIES" subtitle="Welcome to Companies" />

        <Button
          sx={{
            backgroundColor: colors.blueAccent[700],
            color: colors.grey[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 20px",
          }}
          onClick={handleAdd}
        >
          <Add sx={{ mr: "10px" }} />
          Add New Company
        </Button>
      </Stack>

      <Box height="68vh" sx={tableStyle(colors)}>
        <DataGrid
          rows={companiesList}
          columns={columns}
          getRowId={(row) => row._id}
          components={{ Footer: CustomFooter }}
        />
      </Box>

      <CompanyDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        data={dialogData}
        colors={colors}
      />
    </Box>
  );
};

export default CompaniesList;